import type { BoerLandingLocale } from "./boerCopy";

/**
 * BOE-40 contact sheet strings, merged into BOER_LANDING_COPY (so the key-parity
 * test and the runtime's apply() cover them). `{email}` in contact.success is
 * replaced with the visitor's address by the runtime (as text, never HTML).
 *
 * es / it / ja are machine-quality drafts awaiting a native-speaker review.
 */
export const BOER_CONTACT_COPY: Record<BoerLandingLocale, Record<string, string>> = {
  en: {
    "contact.title.packhouse": "Talk to us about your packhouse",
    "contact.title.pricing": "Talk to us about Packhouse & co-op",
    "contact.title.contact": "Contact us",
    "contact.title.startFree": "Register your interest",
    "contact.intro": "Send us a note and we'll get back to you.",
    "contact.introStartFree":
      "Boer's pricing is under investigation. Leave your details and we'll let you know as soon as prices are set.",
    "contact.messageStartFree": "Please let me know when Boer's prices are set.",
    "contact.to": "Sends to",
    "contact.name": "Your name",
    "contact.email": "E-mail",
    "contact.phone": "Phone (optional)",
    "contact.farm": "Farm or packhouse (optional)",
    "contact.message": "Message",
    "contact.send": "Send",
    "contact.sending": "Sending…",
    "contact.close": "Close",
    "contact.done": "Close",
    "contact.required": "Please fill this in.",
    "contact.emailInvalid": "Please enter a valid e-mail address.",
    "contact.phoneInvalid": "Please enter a valid phone number.",
    "contact.tooLong": "This is too long.",
    "contact.success": "Thanks — we'll be in touch at {email}.",
    "contact.error": "Your message could not be sent just now.",
    "contact.rateLimited": "Too many messages from this connection. Please try again in an hour.",
    "contact.errorMail": "E-mail us instead:",
    "contact.mailSubject": "Boer — enquiry from the website",
  },
  af: {
    "contact.title.packhouse": "Praat met ons oor jou pakhuis",
    "contact.title.pricing": "Praat met ons oor Pakhuis & koöp",
    "contact.title.contact": "Kontak ons",
    "contact.title.startFree": "Registreer jou belangstelling",
    "contact.intro": "Stuur vir ons 'n boodskap en ons kom terug na jou.",
    "contact.introStartFree":
      "Boer se pryse word ondersoek. Los jou besonderhede en ons laat weet jou sodra die pryse vasgestel is.",
    "contact.messageStartFree": "Laat weet my asseblief wanneer Boer se pryse vasgestel is.",
    "contact.to": "Stuur aan",
    "contact.name": "Jou naam",
    "contact.email": "E-pos",
    "contact.phone": "Foon (opsioneel)",
    "contact.farm": "Plaas of pakhuis (opsioneel)",
    "contact.message": "Boodskap",
    "contact.send": "Stuur",
    "contact.sending": "Stuur tans…",
    "contact.close": "Maak toe",
    "contact.done": "Maak toe",
    "contact.required": "Vul asseblief hierdie veld in.",
    "contact.emailInvalid": "Tik asseblief 'n geldige e-posadres in.",
    "contact.phoneInvalid": "Tik asseblief 'n geldige foonnommer in.",
    "contact.tooLong": "Dit is te lank.",
    "contact.success": "Dankie — ons sal by {email} met jou in verbinding tree.",
    "contact.error": "Jou boodskap kon nie nou gestuur word nie.",
    "contact.rateLimited": "Te veel boodskappe van hierdie verbinding. Probeer asseblief oor 'n uur weer.",
    "contact.errorMail": "E-pos ons eerder:",
    "contact.mailSubject": "Boer — navraag van die webwerf",
  },
  es: {
    "contact.title.packhouse": "Hablemos de tu planta de empaque",
    "contact.title.pricing": "Hablemos de Planta y coop",
    "contact.title.contact": "Contáctanos",
    "contact.title.startFree": "Registra tu interés",
    "contact.intro": "Escríbenos y te responderemos.",
    "contact.introStartFree":
      "Los precios de Boer están en estudio. Déjanos tus datos y te avisaremos en cuanto estén definidos.",
    "contact.messageStartFree": "Avísenme cuando estén definidos los precios de Boer.",
    "contact.to": "Se envía a",
    "contact.name": "Tu nombre",
    "contact.email": "Correo electrónico",
    "contact.phone": "Teléfono (opcional)",
    "contact.farm": "Finca o planta de empaque (opcional)",
    "contact.message": "Mensaje",
    "contact.send": "Enviar",
    "contact.sending": "Enviando…",
    "contact.close": "Cerrar",
    "contact.done": "Cerrar",
    "contact.required": "Completa este campo.",
    "contact.emailInvalid": "Introduce un correo electrónico válido.",
    "contact.phoneInvalid": "Introduce un número de teléfono válido.",
    "contact.tooLong": "Es demasiado largo.",
    "contact.success": "Gracias — te escribiremos a {email}.",
    "contact.error": "No pudimos enviar tu mensaje en este momento.",
    "contact.rateLimited": "Demasiados mensajes desde esta conexión. Vuelve a intentarlo en una hora.",
    "contact.errorMail": "Escríbenos por correo:",
    "contact.mailSubject": "Boer — consulta desde la web",
  },
  it: {
    "contact.title.packhouse": "Parliamone del tuo magazzino",
    "contact.title.pricing": "Parliamo di Magazzino e coop",
    "contact.title.contact": "Contattaci",
    "contact.title.startFree": "Registra il tuo interesse",
    "contact.intro": "Scrivici e ti risponderemo.",
    "contact.introStartFree":
      "I prezzi di Boer sono in fase di valutazione. Lasciaci i tuoi dati e ti avviseremo appena saranno definiti.",
    "contact.messageStartFree": "Avvisatemi quando i prezzi di Boer saranno definiti.",
    "contact.to": "Destinatario",
    "contact.name": "Il tuo nome",
    "contact.email": "E-mail",
    "contact.phone": "Telefono (facoltativo)",
    "contact.farm": "Azienda o magazzino (facoltativo)",
    "contact.message": "Messaggio",
    "contact.send": "Invia",
    "contact.sending": "Invio in corso…",
    "contact.close": "Chiudi",
    "contact.done": "Chiudi",
    "contact.required": "Compila questo campo.",
    "contact.emailInvalid": "Inserisci un indirizzo e-mail valido.",
    "contact.phoneInvalid": "Inserisci un numero di telefono valido.",
    "contact.tooLong": "Il testo è troppo lungo.",
    "contact.success": "Grazie — ti scriveremo a {email}.",
    "contact.error": "Non è stato possibile inviare il messaggio in questo momento.",
    "contact.rateLimited": "Troppi messaggi da questa connessione. Riprova tra un'ora.",
    "contact.errorMail": "Scrivici via e-mail:",
    "contact.mailSubject": "Boer — richiesta dal sito",
  },
  ja: {
    "contact.title.packhouse": "選果場について相談する",
    "contact.title.pricing": "選果場・協同組合について相談する",
    "contact.title.contact": "お問い合わせ",
    "contact.title.startFree": "関心を登録する",
    "contact.intro": "メッセージをお送りください。担当者からご連絡します。",
    "contact.introStartFree": "Boerの料金は現在検討中です。ご連絡先を残していただければ、料金が決まりしだいお知らせします。",
    "contact.messageStartFree": "Boerの料金が決まったら知らせてください。",
    "contact.to": "送信先",
    "contact.name": "お名前",
    "contact.email": "メールアドレス",
    "contact.phone": "電話番号（任意）",
    "contact.farm": "農場名・選果場名（任意）",
    "contact.message": "メッセージ",
    "contact.send": "送信",
    "contact.sending": "送信中…",
    "contact.close": "閉じる",
    "contact.done": "閉じる",
    "contact.required": "この項目を入力してください。",
    "contact.emailInvalid": "有効なメールアドレスを入力してください。",
    "contact.phoneInvalid": "有効な電話番号を入力してください。",
    "contact.tooLong": "長すぎます。",
    "contact.success": "ありがとうございます — {email} 宛てにご連絡します。",
    "contact.error": "ただいまメッセージを送信できませんでした。",
    "contact.rateLimited": "この接続からの送信が多すぎます。1時間後にもう一度お試しください。",
    "contact.errorMail": "メールでのお問い合わせ：",
    "contact.mailSubject": "Boer — ウェブサイトからのお問い合わせ",
  },
};
